import React from 'react';
import { Arrow } from 'icons/untitledui';
import { StepType } from '@reactour/tour';

// context
import { useUser } from 'shared/hooks/api/user';

// styles
import * as Buttons from 'theme/components/Buttons.style';
import * as Navigation from 'productTours/styles/Navigation.style';

interface TourNavigationProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  currentStep: number;
  steps: StepType[];
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function TourNavigation({
  setCurrentStep,
  currentStep,
  steps,
  setIsOpen,
}: TourNavigationProps): React.ReactElement {
  const { data: user } = useUser();

  // Check if we should hide the next button
  const hideBackButton = currentStep === 0;
  const hideNextButton =
    currentStep === steps.length - 1 ||
    (steps[currentStep]?.styles?.arrow &&
      typeof steps[currentStep].styles.arrow === 'function' &&
      (steps[currentStep].styles.arrow as any)().display === 'none');

  return (
    <Navigation.NavigationContainer>
      {!hideBackButton && user?.is_demo && (
        <Navigation.BackButtonContainer>
          <Buttons.SecondaryDarkIcon
            tiny
            aria-label="Tour back"
            onClick={() => {
              if (currentStep > 0) {
                setCurrentStep(currentStep - 1);
              }
            }}
            disabled={currentStep === 0}
          >
            <Arrow.ArrowLeft size={14} strokeWidth={2} />
          </Buttons.SecondaryDarkIcon>
        </Navigation.BackButtonContainer>
      )}

      {/* Indicator dots - always shown */}
      <Navigation.DotsContainer>
        {steps.map((_, i) => (
          <Navigation.Dot
            key={i}
            active={i === currentStep}
            role="presentation"
            aria-hidden="true"
          />
        ))}
      </Navigation.DotsContainer>

      {/* Next button - conditionally shown */}
      {!hideNextButton && (
        <Navigation.NextButtonContainer>
          <Buttons.PrimaryIcon
            tiny
            aria-label="Tour next"
            onClick={() => {
              if (currentStep < steps.length - 1) {
                setCurrentStep(currentStep + 1);
              } else {
                setIsOpen(false);
              }
            }}
            disabled={currentStep === steps.length - 1}
          >
            <Arrow.ArrowRight size={14} strokeWidth={2} />
          </Buttons.PrimaryIcon>
        </Navigation.NextButtonContainer>
      )}
    </Navigation.NavigationContainer>
  );
}
